"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useColumnChoice = useColumnChoice;
const react_1 = require("react");
function useColumnChoice(columns, storageKey) {
    /* ⚠️ Read once per mount; from then on the table owns the choice and writes it back. */
    const [remembered] = (0, react_1.useState)(() => readColumns(storageKey));
    const [hidden, setHidden] = (0, react_1.useState)(() => remembered?.hidden ??
        columns.filter((column) => column.defaultHidden === true).map((column) => column.key));
    const [order, setOrder] = (0, react_1.useState)(() => remembered?.order ?? null);
    /** Keys this browser has decided about — any other column falls back to its `defaultHidden`. */
    const [known, setKnown] = (0, react_1.useState)(() => remembered?.known ?? []);
    (0, react_1.useEffect)(() => {
        writeColumns(storageKey, { hidden, order, known });
    }, [storageKey, hidden, order, known]);
    const effectiveHidden = [
        ...hidden,
        ...columns
            .filter((column) => column.defaultHidden === true &&
            !known.includes(column.key) &&
            !hidden.includes(column.key))
            .map((column) => column.key),
    ];
    /* Unknown keys are dropped and new ones append, so adding a column needs no stored order cleared. */
    const ordered = order === null
        ? columns
        : [
            ...order.flatMap((key) => columns.filter((column) => column.key === key)),
            ...columns.filter((column) => !order.includes(column.key)),
        ];
    return {
        ordered,
        visible: ordered.filter((column) => !effectiveHidden.includes(column.key)),
        hidden: effectiveHidden,
        toggle: (key) => {
            const now = effectiveHidden.includes(key);
            setHidden(now ? effectiveHidden.filter((one) => one !== key) : [...effectiveHidden, key]);
            setKnown(columns.map((column) => column.key));
        },
        move: (key, to) => {
            const keys = ordered.map((column) => column.key);
            const at = keys.indexOf(key);
            if (at === -1 || to < 0 || to >= keys.length || to === at)
                return;
            const next = keys.filter((one) => one !== key);
            next.splice(to, 0, key);
            setOrder(next);
        },
    };
}
const COLUMNS_PREFIX = 'forecastify.columns.';
/**
 * ⚠️ **Browser storage, and every access guarded.** A private window, blocked site data or a
 * quota can make it throw; the table then simply forgets, which is what it did before.
 */
function readColumns(key) {
    if (key === undefined)
        return null;
    try {
        const raw = window.localStorage.getItem(COLUMNS_PREFIX + key);
        if (raw === null)
            return null;
        const value = JSON.parse(raw);
        const strings = (list) => Array.isArray(list) && list.every((one) => typeof one === 'string') ? list : null;
        return {
            hidden: strings(value.hidden) ?? [],
            order: strings(value.order),
            known: strings(value.known) ?? [],
        };
    }
    catch {
        return null;
    }
}
function writeColumns(key, value) {
    if (key === undefined)
        return;
    try {
        window.localStorage.setItem(COLUMNS_PREFIX + key, JSON.stringify(value));
    }
    catch {
        /* Forgetting is the fallback — see `readColumns`. */
    }
}
//# sourceMappingURL=column-choice.js.map